import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useSEO, BASE } from "@/lib/seo";
import ArticleShell, { H2, P, SeeAlso } from "@/components/site/ArticleShell";
import { Reveal } from "@/components/site/Reveal";

const OG_IMAGE = `${BASE}/images/reseau-metiers-art-sculpture-og.jpg`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METIERS_OPTIONS = [
  { value: "", label: "Choisir votre métier" },
  { value: "fonderie", label: "Fonderie" },
  { value: "taille de pierre", label: "Taille de pierre" },
  { value: "ferronnerie", label: "Ferronnerie" },
  { value: "résine", label: "Résine" },
  { value: "moulage", label: "Moulage" },
  { value: "patine", label: "Patine" },
  { value: "bois", label: "Bois" },
  { value: "céramique, poterie, terre", label: "Céramique, poterie, terre" },
  { value: "autre", label: "Autre" },
];

const EMPTY_FORM = { atelier: "", metier: "", ville: "", site: "", email: "", message: "" };

function InscriptionForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/annuaire`, form);
      setSent(true);
      setForm(EMPTY_FORM);
      toast.success("Demande d'inscription enregistrée.");
    } catch {
      toast.error("L'envoi a échoué.", { description: "Vérifiez les champs obligatoires et réessayez." });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-ink/20 bg-paper px-4 py-3.5 text-base font-light text-ink outline-none transition-colors duration-300 placeholder:text-smoke/50 focus:border-bronze";
  const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke";

  return (
    <form
      onSubmit={submit}
      id="formulaire-inscription"
      data-testid="annuaire-form"
      className="mt-8 scroll-mt-32 border border-ink/15 bg-cream p-8 lg:p-12"
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="annuaire-atelier" className={labelCls}>Nom de l'atelier ou de l'entreprise *</label>
          <input id="annuaire-atelier" data-testid="annuaire-atelier-input" required maxLength={160}
            value={form.atelier} onChange={set("atelier")} placeholder="Atelier Dupont" className={inputCls} />
        </div>
        <div>
          <label htmlFor="annuaire-metier" className={labelCls}>Métier *</label>
          <select id="annuaire-metier" data-testid="annuaire-metier-select" required
            value={form.metier} onChange={set("metier")} className={inputCls}>
            {METIERS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} disabled={o.value === ""}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="annuaire-ville" className={labelCls}>Ville *</label>
          <input id="annuaire-ville" data-testid="annuaire-ville-input" required maxLength={120}
            value={form.ville} onChange={set("ville")} placeholder="Lyon" className={inputCls} />
        </div>
        <div>
          <label htmlFor="annuaire-site" className={labelCls}>Site internet (facultatif)</label>
          <input id="annuaire-site" data-testid="annuaire-site-input" type="url" maxLength={300}
            value={form.site} onChange={set("site")} placeholder="https://votre-site.fr" className={inputCls} />
        </div>
      </div>
      <div className="mt-6">
        <label htmlFor="annuaire-email" className={labelCls}>E-mail *</label>
        <input id="annuaire-email" data-testid="annuaire-email-input" type="email" required maxLength={200}
          value={form.email} onChange={set("email")} placeholder="vous@votre-atelier.fr" className={inputCls} />
      </div>
      <div className="mt-6">
        <label htmlFor="annuaire-message" className={labelCls}>Message (facultatif)</label>
        <textarea id="annuaire-message" data-testid="annuaire-message-textarea" maxLength={4000} rows={5}
          value={form.message} onChange={set("message")}
          placeholder="Votre activité, vos techniques, vos réalisations…" className={`${inputCls} resize-y`} />
      </div>
      <button type="submit" data-testid="annuaire-submit-button" disabled={sending}
        className="group mt-8 inline-flex items-center gap-3 bg-ink px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze disabled:opacity-50">
        {sending ? "Envoi en cours…" : "Inscrire gratuitement mon atelier"}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      {sent && (
        <p data-testid="annuaire-success-message" className="mt-5 border-l-2 border-bronze pl-4 text-sm font-light text-body">
          Merci ! Votre demande d'inscription gratuite est bien reçue, nous revenons vers vous rapidement.
        </p>
      )}
    </form>
  );
}

export default function AnnuairePage() {
  useSEO(
    "Inscription gratuite des artisans d'art | Sculpteur Français",
    "Artisans et ateliers d'art : inscrivez gratuitement votre atelier dans l'annuaire des métiers d'art de Sculpteur Français. Fiche, photos et lien vers votre site.",
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: "Artisans et ateliers d'art : votre fiche gratuite dans l'annuaire des métiers d'art",
      description:
        "Artisans et ateliers d'art : inscrivez gratuitement votre atelier dans l'annuaire des métiers d'art de Sculpteur Français. Fiche, photos et lien vers votre site.",
      inLanguage: "fr-FR",
      datePublished: "2026-09-30",
      dateModified: "2026-10-18",
      image: OG_IMAGE,
      mainEntityOfPage: `${BASE}/devenez-partenaire-metiers-d-art`,
      author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
      publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
    },
    {
      type: "article",
      image: OG_IMAGE,
      ogTitle: "Inscription gratuite des artisans d'art — annuaire des métiers d'art",
      ogDescription:
        "Artisans et ateliers d'art : inscrivez gratuitement votre atelier dans l'annuaire des métiers d'art de Sculpteur Français. Fiche, photos et lien vers votre site.",
      breadcrumb: [{ name: "Annuaire des métiers d'art", path: "/devenez-partenaire-metiers-d-art" }],
    }
  );

  return (
    <ArticleShell
      testId="annuaire-page"
      eyebrow="Annuaire — savoir-faire"
      titleLines={["Artisans et ateliers d'art :", "votre fiche gratuite dans", "l'annuaire des métiers d'art"]}
      lead="Artisans et ateliers d'art : l'inscription de votre fiche dans notre annuaire des métiers d'art est gratuite. Derrière chaque sculpture d'artiste, il y a des femmes, des hommes et des entreprises qui maîtrisent des savoir-faire parfois rares : fonderie, moulage, travail de la résine, métal, pierre, finition, chromage, patines… Ces métiers sont essentiels à la création des œuvres, mais restent souvent peu visibles, aussi bien du grand public que des artistes eux-mêmes."
    >
      <H2>Pourquoi un annuaire des métiers d'art de la sculpture ?</H2>
      <P>
        Nous ouvrons aujourd'hui notre espace éditorial à ces professionnels, en construisant
        progressivement un annuaire des métiers d'art liés à la sculpture. L'objectif est simple :
        faire connaître ces savoir-faire, mettre en lumière ceux qui les exercent, et permettre aux
        sculpteurs, collectionneurs et amateurs d'art de découvrir les artisans et entreprises qui
        participent à la réalisation des œuvres — et peut-être d'y trouver le professionnel qu'ils
        recherchent.
      </P>

      <Reveal className="mt-12">
        <figure data-testid="annuaire-figure">
          <div className="img-content-wrap">
            <img
              src="/images/reseau-metiers-art-sculpture.png"
              alt="Schéma en étoile : Sculpteur Français relié par des partenariats réciproques aux métiers d'art de la sculpture — fonderie, ferronnerie, taille de pierre, résine et moulage, chromage et finition"
              className="img-content border border-ink/15"
              loading="lazy"
            />
          </div>
          <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
            Le réseau des métiers d'art de la sculpture réuni autour de Sculpteur Français
          </figcaption>
        </figure>
      </Reveal>

      <H2>Une inscription gratuite, ouverte à tous les artisans et ateliers d'art</H2>
      <P>
        Nous proposons aux fonderies d'art, ferronniers, chromeurs, tailleurs de pierre, fabricants
        de résine et de moules — sans restriction — de rejoindre cet annuaire, entièrement
        gratuitement. Vous nous parlez de votre activité avec vos propres mots : votre histoire, vos
        techniques, ce qui vous distingue, vos plus belles réalisations. Ajoutez une ou deux photos
        et vos liens de contact, nous nous chargeons de la mise en forme.
      </P>

      <H2>Comment rejoindre l'annuaire ?</H2>
      <p className="mt-5 text-base font-light leading-relaxed text-body sm:text-lg">
        En échange, nous vous demandons une seule chose : insérer sur votre site un petit encart
        renvoyant vers sculpteur-francais.com. Remplissez simplement le formulaire ci-dessous —
        c'est tout.
      </p>

      <InscriptionForm />

      <H2>Un partenariat sans engagement</H2>
      <P>
        Aucun engagement de durée : vous pouvez mettre fin au partenariat à tout moment, en nous
        prévenant simplement par politesse.
      </P>

      <P>
        L'inscription gratuite est ouverte à tout artisan d'art comme à tout atelier d'art :
        écrivez-nous quelques lignes sur votre activité, votre fiche est mise en ligne dès
        validation.
      </P>
      <p className="mt-8">
        <a
          href="#formulaire-inscription"
          data-testid="annuaire-inscription-btn"
          className="group inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
        >
          Inscrire gratuitement mon atelier
        </a>
      </p>

      <p className="mt-12 font-serif text-xl italic text-ink" data-testid="annuaire-signature">
        Au plaisir d'échanger avec vous,
        <br />
        <span className="text-bronze">L'équipe de sculpteur-francais.com</span>
      </p>

      <SeeAlso
        links={[
          { to: "/partenaires-metiers-d-art", label: "Découvrir nos partenaires actuels" },
          { to: "/materiaux-et-techniques", label: "Matériaux & techniques de la sculpture" },
          { to: "/lexique-sculpture", label: "Le lexique de la sculpture" },
        ]}
      />
    </ArticleShell>
  );
}
